import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { DESK } from "../../constants/testIds";
import { useCelebration } from "../../contexts/CelebrationContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { formatCurrency } from "../../lib/format";
import { toast } from "../ui/sonner";
import { SubmitButton } from "../common/SubmitButton";
import { useSuccessPulse } from "../../hooks/useSuccessPulse";

// UX-FD-01 §1 SCR-P02-13: Renew (same plan, extends by its duration) and Upgrade/change-plan
// (switch plans, server prorates the difference) share one modal since both just need the
// membership's `expected_version` for optimistic-concurrency safety.
export function RenewModal({ member, membership, open, onOpenChange }) {
  const [mode, setMode] = useState("renew"); // "renew" | "change_plan"
  const [newPlanId, setNewPlanId] = useState("");
  const queryClient = useQueryClient();
  const { celebrate } = useCelebration();
  const [success, pulse] = useSuccessPulse();

  const plansQ = useQuery({
    queryKey: ["desk-plans"],
    queryFn: async () => (await api.get("/plans")).data.plans,
    enabled: open && mode === "change_plan",
  });

  const mutation = useMutation({
    mutationFn: () => {
      const key = { headers: { "Idempotency-Key": crypto.randomUUID() } };
      if (mode === "renew") {
        return api.post(`/memberships/${membership.id}/renew`, { expected_version: membership._sync.version }, key);
      }
      return api.post(`/memberships/${membership.id}/change-plan`, { expected_version: membership._sync.version, new_plan_ref: newPlanId }, key);
    },
    onSuccess: () => pulse(() => {
      if (mode === "renew") celebrate("membership_renewed", `${member?.profile?.name}'s membership is renewed.`);
      else toast.success("Plan changed.");
      queryClient.invalidateQueries({ queryKey: ["desk-member", member.id] });
      onOpenChange(false);
    }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal">
        <DialogHeader>
          <DialogTitle>Renew or change plan</DialogTitle>
          <DialogDescription>{member?.profile?.name}'s current membership.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button size="sm" variant={mode === "renew" ? "default" : "outline"} onClick={() => setMode("renew")} className={mode === "renew" ? "text-white" : ""} style={mode === "renew" ? { background: "#D6202B" } : undefined}>
            Renew same plan
          </Button>
          <Button size="sm" variant={mode === "change_plan" ? "default" : "outline"} onClick={() => setMode("change_plan")} className={mode === "change_plan" ? "text-white" : ""} style={mode === "change_plan" ? { background: "#D6202B" } : undefined}>
            Change plan
          </Button>
        </div>
        {mode === "change_plan" && (
          <Select onValueChange={setNewPlanId} value={newPlanId}>
            <SelectTrigger data-testid={DESK.changePlanSelect}><SelectValue placeholder="Choose new plan *" /></SelectTrigger>
            <SelectContent>
              {plansQ.data?.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name} — {formatCurrency(p.pricing.amount)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <DialogFooter>
          <SubmitButton
            testId={mode === "renew" ? DESK.renewSubmitButton : DESK.changePlanSubmitButton}
            disabled={mode === "change_plan" && !newPlanId}
            pending={mutation.isPending} success={success}
            onClick={() => mutation.mutate()}
            label={mode === "renew" ? "Renew" : "Change plan"} successLabel={mode === "renew" ? "Renewed" : "Changed"}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
